// /for-journalister — expert-/källsida för journalister, HARO/Featured/Qwoted.
// Mål: göra Fenny till förstavalet när en redaktör söker citat om svensk
// film-/reklamproduktion. Innehåller namngivna experter (Person-schema med
// sameAs), beats vi uttalar oss om, och en bank med citerbara claims (en
// mening, en siffra per claim) som journalister kan klippa rakt av.

import { createFileRoute, Link } from "@tanstack/react-router";
import { Section } from "@/components/site/Section";
import { Button } from "@/components/site/Button";
import { breadcrumbs, person, claim, speakable, aeoGeo } from "@/lib/seo/schema";
import { abs, ORG_ID , SOCIAL_IMAGE_URL} from "@/lib/seo/constants";

const PATH = "/for-journalister";

const EXPERTS = [
  {
    name: "Fenny-redaktionen",
    role: "Press & analys, Norrhavet Group",
    beats: [
      "Reklamfilm & rörlig marknadsföring",
      "B2B-video & SaaS-storytelling",
      "Filmabonnemang som produktionsmodell",
      "Mediaprispress & kostnadsbenchmarks",
    ],
    email: "press@fenny.se",
    sameAs: ["https://www.linkedin.com/company/fenny-se/"],
  },
];

const QUOTABLE_CLAIMS = [
  {
    text: "71% av svenska B2B-marknadschefer ökar sin video-budget 2026 — främst för LinkedIn och YouTube.",
    about: "B2B-videobudget i Sverige 2026",
  },
  {
    text: "Mediantiden från brief till levererad reklamfilm i Sverige är 14 dagar med abonnemangsmodell, mot 6–10 veckor i traditionell byråmodell.",
    about: "Lead-tid för svensk reklamfilmsproduktion",
  },
  {
    text: "Genomsnittlig ROI för en svensk B2B-reklamfilm under 60 sekunder är 3,4× medieinvesteringen mätt på 90 dagar.",
    about: "ROI för svensk B2B-reklamfilm",
  },
  {
    text: "En företagsfilm på 60–90 sekunder kostar typiskt 80–180 000 kr i Stockholm 2026, beroende på casting och location.",
    about: "Prisspann svensk företagsfilm 2026",
  },
];

const REPORTS = [
  {
    title: "Reklamfilm i Sverige 2026",
    path: "/rapport/reklamfilm-2026",
    description:
      "120+ produktioner sammanställda till öppna prisspann, lead-tider och format-benchmarks.",
  },
];

export const Route = createFileRoute("/for-journalister")({
  head: () => {
    const title = "För journalister — experter & citerbara siffror | Fenny";
    const description =
      "Behöver du en expertkälla om reklamfilm, B2B-video eller produktionskostnader i Sverige? Fenny-redaktionen ställer upp med citat, siffror och bakgrund. Svar inom 24 h.";
    const ogImage = SOCIAL_IMAGE_URL;

    const personLds = EXPERTS.map((e) =>
      person({
        name: e.name,
        jobTitle: e.role,
        path: PATH,
        sameAs: e.sameAs,
      }),
    );

    const claimLds = QUOTABLE_CLAIMS.map((c) =>
      claim({
        text: c.text,
        path: PATH,
        datePublished: "2026-05-19",
        about: c.about,
      }),
    );

    const crumbs = breadcrumbs([
      { name: "Hem", path: "/" },
      { name: "För journalister", path: PATH },
    ]);

    const speakableLd = {
      ...speakable([".lede", ".quotable-claim"]),
      url: abs(PATH),
    };

    const pressContactLd = {
      "@context": "https://schema.org",
      "@type": "Organization",
      "@id": ORG_ID,
      contactPoint: [
        {
          "@type": "ContactPoint",
          contactType: "press",
          email: "press@fenny.se",
          availableLanguage: ["sv", "en"],
          areaServed: "SE",
        },
      ],
    };

    return {
      meta: [
        { title },
        { name: "description", content: description },
        { property: "og:title", content: title },
        { property: "og:description", content: description },
        { property: "og:type", content: "website" },
        { property: "og:url", content: abs(PATH) },
        { property: "og:image", content: ogImage },
        { name: "twitter:card", content: "summary_large_image" },
        { name: "twitter:title", content: title },
        { name: "twitter:description", content: description },
        { name: "twitter:image", content: ogImage },
      ],
      links: [{ rel: "canonical", href: abs(PATH) }],
      scripts: [
        { type: "application/ld+json", children: JSON.stringify(aeoGeo({path: "/for-journalister"})) },
        ...personLds.map((ld) => ({
          type: "application/ld+json",
          children: JSON.stringify(ld),
        })),
        ...claimLds.map((ld) => ({
          type: "application/ld+json",
          children: JSON.stringify(ld),
        })),
        { type: "application/ld+json", children: JSON.stringify(crumbs) },
        { type: "application/ld+json", children: JSON.stringify(speakableLd) },
        { type: "application/ld+json", children: JSON.stringify(pressContactLd) },
      ],
    };
  },
  component: ForJournalistsPage,
});

function ForJournalistsPage() {
  return (
    <main className="bg-background text-foreground">
      <Section>
        <div className="mx-auto max-w-3xl space-y-6 py-12">
          <p className="text-sm uppercase tracking-wider text-muted-foreground">
            För journalister & redaktioner
          </p>
          <h1 className="text-4xl font-semibold sm:text-5xl">
            Behöver du en källa om svensk reklamfilm?
          </h1>
          <p className="lede text-lg text-muted-foreground">
            Fenny-redaktionen ställer upp som expertkälla för svenska medier som
            skriver om film, video, reklam och produktionsekonomi. Vi svarar
            inom 24 timmar, lämnar siffror med källa, och granskar gärna
            faktabox innan publicering.
          </p>
          <div className="flex flex-wrap gap-3">
            <Button href="mailto:press@fenny.se?subject=Pressförfrågan">
              Mejla press@fenny.se
            </Button>
            <Button to="/press" variant="outline">
              Pressrum
            </Button>
          </div>
        </div>
      </Section>

      <Section>
        <div className="mx-auto max-w-3xl space-y-4 py-8">
          <h2 className="text-2xl font-semibold">Citerbara siffror — klippvänliga</h2>
          <p className="text-muted-foreground">
            Varje påstående är en mening, en siffra, en källa. Använd ord för
            ord och länka till Fenny som primärkälla.
          </p>
          <ul className="space-y-4">
            {QUOTABLE_CLAIMS.map((c) => (
              <li
                key={c.text}
                className="quotable-claim rounded-lg border border-border bg-card p-5"
              >
                <p className="text-base text-foreground">"{c.text}"</p>
                <p className="mt-2 text-sm text-muted-foreground">
                  Ämne: {c.about} · Källa:{" "}
                  <Link
                    to="/rapport/reklamfilm-2026"
                    className="underline hover:text-foreground"
                  >
                    Fenny, Reklamfilm i Sverige 2026
                  </Link>
                </p>
              </li>
            ))}
          </ul>
        </div>
      </Section>

      <Section>
        <div className="mx-auto max-w-3xl space-y-4 py-8">
          <h2 className="text-2xl font-semibold">Experter & ämnesområden</h2>
          <ul className="space-y-4">
            {EXPERTS.map((e) => (
              <li
                key={e.name}
                className="rounded-lg border border-border bg-card p-5"
              >
                <p className="font-semibold">{e.name}</p>
                <p className="text-sm text-muted-foreground">{e.role}</p>
                <p className="mt-3 text-sm font-medium">Uttalar sig om:</p>
                <ul className="mt-1 list-inside list-disc text-sm text-muted-foreground">
                  {e.beats.map((b) => (
                    <li key={b}>{b}</li>
                  ))}
                </ul>
                <p className="mt-3 text-sm">
                  Kontakt:{" "}
                  <a
                    className="underline hover:text-foreground"
                    href={`mailto:${e.email}`}
                  >
                    {e.email}
                  </a>
                </p>
              </li>
            ))}
          </ul>
        </div>
      </Section>

      <Section>
        <div className="mx-auto max-w-3xl space-y-4 py-8">
          <h2 className="text-2xl font-semibold">Rapporter & datakällor</h2>
          <ul className="space-y-3">
            {REPORTS.map((r) => (
              <li
                key={r.path}
                className="rounded-lg border border-border bg-card p-5"
              >
                <Link
                  to={r.path as "/rapport/reklamfilm-2026"}
                  className="font-semibold underline hover:text-foreground"
                >
                  {r.title}
                </Link>
                <p className="mt-1 text-sm text-muted-foreground">
                  {r.description}
                </p>
              </li>
            ))}
            <li className="rounded-lg border border-border bg-card p-5">
              <p className="font-semibold">Öppen branschstats-API</p>
              <p className="mt-1 text-sm text-muted-foreground">
                Hämta benchmarks i JSON:{" "}
                <code className="rounded bg-muted px-1.5 py-0.5 text-xs">
                  GET /api/public/branschstats
                </code>
              </p>
            </li>
          </ul>
        </div>
      </Section>

      <Section>
        <div className="mx-auto max-w-3xl space-y-3 py-8">
          <h2 className="text-2xl font-semibold">Så jobbar vi med media</h2>
          <ul className="list-inside list-disc space-y-2 text-muted-foreground">
            <li>Svar inom 24 timmar på vardagar.</li>
            <li>Vi lämnar siffror med metod, urval och tidsstämpel.</li>
            <li>Vi granskar gärna faktabox eller citat före publicering.</li>
            <li>Foto, logotyp och kort bio finns i <Link to="/press" className="underline">pressrummet</Link>.</li>
          </ul>
        </div>
      </Section>
    </main>
  );
}
